import {
  inject,
  InjectionKey,
  provide,
  ref,
  Ref,
  onMounted,
  watch,
  readonly,
  computed,
  toRaw,
} from 'vue';

import {
  routeKey,
  pageKey,
  navigateKey,
  getServerItemKey,
  getItemKey,
  subscribeKey,
  unsubscribeKey,
  Route,
} from '@drapejs/core';

import useContext from '@/composables/useContext';
import useTycka from '@/composables/useTycka';

export interface ProductData extends Record<string, any> {
  variants: ProductVariant[];
  selectedVariant: ProductVariant;
}

export interface ProductVariant extends Record<string, any> {
  id: string;
}

export const productDataKey: InjectionKey<Ref<ProductData>> =
  Symbol('productData');
export const selectedVariantKey: InjectionKey<Ref<ProductVariant>> =
  Symbol('selectedVariant');
export const canAddToCartKey: InjectionKey<Ref<boolean>> =
  Symbol('canAddToCart');
export const setSelectedVariantKey: InjectionKey<
  (variant: ProductVariant) => void
> = Symbol('setSelectedVariant');
export const getUrlForVariantKey: InjectionKey<
  (variant: ProductVariant) => string
> = Symbol('getUrlForVariant');
export const productReviewsKey: InjectionKey<Ref<any>> =
  Symbol('productReviews');

export const loadMoreProductReviewsKey: InjectionKey<() => void> = Symbol(
  'loadMoreProductReviews'
);

const reviewsTakeCount = 6;

export default function () {
  const navigate = inject(navigateKey, () => Promise.resolve());
  const route = inject(routeKey, <Route>(<any>{}));
  const page = inject(pageKey, ref<any>());
  const cache = {
    getServerItem: inject(getServerItemKey, () => null),
    getItem: inject(getItemKey, () => Promise.resolve(null)),
    subscribe: inject(subscribeKey, () => Promise.resolve()),
    unsubscribe: inject(unsubscribeKey, () => Promise.resolve()),
  };

  let productDataCacheKey = getProductCacheKey(page.value.articleNumber);
  const { channel } = useContext();
  const { getReviewsByProduct } = useTycka();

  const productData = ref<ProductData>({
    ...(cache.getServerItem(productDataCacheKey) || {}),
  });

  let selectedVariantCacheKey = getVariantCacheKey(
    page.value.articleNumber,
    route.query.variant || ''
  );
  const selectedVariant = ref<ProductVariant>(
    cache.getServerItem(selectedVariantCacheKey)
  );

  let reviewsTake = reviewsTakeCount;
  const reviews = ref();
  const requestPending = ref(false);

  const canAddToCart = computed(() => {
    const physicalStock =
      selectedVariant.value?.stock?.currentStock?.amount || 0;
    const totalStockAvailable =
      selectedVariant.value?.stock?.totalStockAvailable || 0;
    const allowOrderingAgainstIncomingStock =
      channel.value?.allowOrderingAgainstIncomingStock || false;

    if (!selectedVariant.value) return false;
    if (physicalStock <= 0) {
      return totalStockAvailable > 0 && allowOrderingAgainstIncomingStock;
    }
    return true;
  });

  provide(productDataKey, productData);
  provide(selectedVariantKey, readonly(selectedVariant));
  provide(productReviewsKey, readonly(reviews));

  provide(canAddToCartKey, canAddToCart);
  provide(setSelectedVariantKey, setSelectedVariant);
  provide(getUrlForVariantKey, getUrlForVariant);
  provide(loadMoreProductReviewsKey, loadMoreReviews);

  async function loadReviewsByProduct() {
    try {
      requestPending.value = true;
      const response = await getReviewsByProduct({
        baseProductId: page.value.articleNumber,
        take: reviewsTake,
      });
      reviews.value = response;
    } finally {
      requestPending.value = false;
    }
  }

  async function loadMoreReviews() {
    if (requestPending.value) return;
    reviewsTake += reviewsTakeCount;
    await loadReviewsByProduct();
  }

  onMounted(async () => {
    watch(
      () => page.value.articleNumber,
      async () => {
        if (productDataCacheKey) {
          cache.unsubscribe(productDataCacheKey, cacheHandler);
        }

        productDataCacheKey = getProductCacheKey(page.value.articleNumber);
        cache.subscribe(productDataCacheKey, cacheHandler, { immediate: true });

        if (!reviews.value) {
          await loadReviewsByProduct();
        }
      },
      { immediate: true }
    );

    watch(
      () => route.query.variant,
      () => {
        if (selectedVariantCacheKey) {
          cache.unsubscribe(selectedVariantCacheKey, variantCacheHandler);
        }

        selectedVariantCacheKey = getVariantCacheKey(
          page.value.articleNumber,
          route.query.variant || ''
        );
        cache.subscribe(selectedVariantCacheKey, variantCacheHandler, {
          immediate: true,
        });
      },
      { immediate: true }
    );
  });

  function toQueryString(queryParams: { [key: string]: string | null }) {
    const queryItems: string[] = [];
    if (queryParams) {
      for (const key of Object.keys(queryParams).sort()) {
        const value = queryParams[key];
        if (value) {
          queryItems.push(`${key}=${value}`);
        } else {
          queryItems.push(`${key}`);
        }
      }
    }

    if (queryItems.length > 0) {
      return `?${queryItems.join('&')}`;
    }
    return '';
  }

  function getUrlForVariant(variant: ProductVariant) {
    const query = Object.assign({}, toRaw(route.query));

    if (variant) {
      query.variant = variant.id || variant.articleNumber;
    } else {
      delete query.variant;
    }

    return route.pathname + toQueryString(query);
  }

  function setSelectedVariant(variant: ProductVariant, replaceHistory = false) {
    navigate(getUrlForVariant(variant), { replaceHistory });
  }

  function cacheHandler(val: any) {
    productData.value = val;
  }

  function variantCacheHandler(val: any) {
    selectedVariant.value = val.selectedVariant;
  }

  return {
    productData: readonly(productData),
    selectedVariant: readonly(selectedVariant),
    setSelectedVariant,
  };
}

export function getContext() {
  return {
    productData: inject(productDataKey, ref<ProductData>(<any>{})),
    selectedVariant: inject(selectedVariantKey, ref<ProductVariant>(<any>{})),
    canAddToCart: inject(canAddToCartKey, ref(false)),
    setSelectedVariant: inject(setSelectedVariantKey, () => null),
    getUrlForVariant: inject(getUrlForVariantKey, () => ''),
    reviews: inject(productReviewsKey, ref<any>(<any>{})),
    loadMoreReviews: inject(loadMoreProductReviewsKey, () => null),
  };
}

export function getProductCacheKey(baseProductId: string) {
  return `product:${baseProductId}`;
}

export function getVariantCacheKey(baseProductId: string, variantId: string) {
  return `product:${baseProductId}::${variantId}`;
}
